import {DeleteOutlined, PlusOutlined} from '@ant-design/icons'
import {Button, InputNumber, Space} from 'antd'
import React, {LegacyRef, MutableRefObject} from 'react'
import {Transformer} from 'react-konva'
import {TransformerUIProps} from './TransformerUI'
import {useIntl} from 'react-intl'

export function calculateEdges(points: number[]) {
  let edges = []
  let xDif = 0
  let yDif = 0
  for (let i = 0; i < points.length - 1; i += 2) {
    if (i === points.length - 2) {
      xDif = points[i] - points[0]
      yDif = points[i + 1] - points[1]
    } else {
      xDif = points[i] - points[i + 2]
      yDif = points[i + 1] - points[i + 3]
    }
    edges.push(Math.sqrt(xDif * xDif + yDif * yDif))
  }
  //console.log(edges)
  return edges as number[]
}

export function deletePoint(edgeNumber: number, points: number[]) {
  let newPoints = [...points]
  if ((edgeNumber + 1) * 2 === points.length) {
    newPoints.splice(0, 2)
  } else {
    newPoints.splice(edgeNumber * 2 + 2, 2)
  }
  return newPoints as number[]
}

export function addPoint(edgeNumber: number, points: number[]) {
  let newPoints = [...points]
  if ((edgeNumber + 1) * 2 === points.length) {
    let x = (points[0] - points[edgeNumber * 2]) / 2 + points[edgeNumber * 2]
    let y = (points[1] - points[edgeNumber * 2 + 1]) / 2 + points[edgeNumber * 2 + 1]
    newPoints.splice(0, 0, x, y)
  } else {
    let x = (points[edgeNumber * 2 + 2] - points[edgeNumber * 2]) / 2 + points[edgeNumber * 2]
    let y =
      (points[edgeNumber * 2 + 3] - points[edgeNumber * 2 + 1]) / 2 + points[edgeNumber * 2 + 1]
    newPoints.splice(edgeNumber * 2 + 2, 0, x, y)
  }

  return newPoints as number[]
}

export function recalculateEdgeRightVertice(
  edgeNumber: number,
  newValue: number,
  points: number[]
) {
  let newPoints = [...points]
  if ((edgeNumber + 1) * 2 === points.length) {
    let firstVerticeX = points[edgeNumber * 2]
    let firstVerticeY = points[edgeNumber * 2 + 1]
    let secondVerticeX = points[0]
    let secondVerticeY = points[1]
    let oldLength = Math.sqrt(
      (secondVerticeX - firstVerticeX) ^ (2 + (secondVerticeY - firstVerticeY)) ^ 2
    )
    let x = (newValue * (secondVerticeX - firstVerticeX)) / oldLength + firstVerticeX
    let y = (newValue * (secondVerticeY - firstVerticeY)) / oldLength + firstVerticeY
    newPoints.splice(0, 2, x, y)
  } else {
    let firstVerticeX = points[edgeNumber * 2]
    let firstVerticeY = points[edgeNumber * 2 + 1]
    let secondVerticeX = points[edgeNumber * 2 + 2]
    let secondVerticeY = points[edgeNumber * 2 + 3]
    let oldLength = Math.sqrt(
      (secondVerticeX - firstVerticeX) * (secondVerticeX - firstVerticeX) +
        (secondVerticeY - firstVerticeY) * (secondVerticeY - firstVerticeY)
    )
    let x = (newValue * (secondVerticeX - firstVerticeX)) / oldLength + firstVerticeX
    let y = (newValue * (secondVerticeY - firstVerticeY)) / oldLength + firstVerticeY
    newPoints.splice(edgeNumber * 2 + 2, 2, x, y)
  }

  return newPoints as number[]
}

export function PolygonTransformerUI(props: TransformerUIProps<{highlightedEdge: number[]}>) {
  let sides = calculateEdges(props.element.props.points)
  const intl = useIntl()

  const highlightEdge = (index: number, points: number[]) => {
    props.onTransformerPropsChange({
      highlightedEdge: [...points, ...points].slice(index * 2, index * 2 + 4),
    })
  }

  return (
    <div>
      <Space direction="vertical">
        {sides.map((side, index) => (
          <div key={index} style={{display: 'flex', flexDirection: 'row'}}>
            <InputNumber
              addonBefore={`${intl.formatMessage({id: 'edge'})} ${index + 1}`}
              addonAfter={intl.formatMessage({id: 'm'})}
              precision={2}
              step={0.01}
              value={Math.round(side) / 100}
              onChange={(value) => {
                const newPoints = recalculateEdgeRightVertice(
                  index,
                  value * 100,
                  props.element.props.points
                )
                props.onChange({
                  ...props.element,
                  props: {
                    ...props.element.props,
                    points: newPoints,
                  },
                })
                highlightEdge(index, newPoints)
              }}
              onFocus={() => highlightEdge(index, props.element.props.points)}
              onBlur={() => props.onTransformerPropsChange({highlightedEdge: null})}
            />
            {props.element.props.points.length > 6 && (
              <Button
                type="link"
                style={{float: 'right'}}
                onClick={async () => {
                  props.onChange({
                    ...props.element,
                    props: {
                      ...props.element.props,
                      points: deletePoint(index, props.element.props.points),
                    },
                  })
                }}
              >
                <DeleteOutlined />
              </Button>
            )}

            <Button
              type="link"
              style={{float: 'right'}}
              onClick={async () => {
                props.onChange({
                  ...props.element,
                  props: {
                    ...props.element.props,
                    points: addPoint(index, props.element.props.points),
                  },
                })
              }}
            >
              <PlusOutlined />
            </Button>
          </div>
        ))}
      </Space>
    </div>
  )
}
